<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ isEdit ? $t("modern_trade.sale_order.edit_title") : $t("modern_trade.sale_order.create") }}
            <span v-if="soNo" class="ml-2">{{ soNo }}</span>
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button size="sm" type="default" @click="goBack">
            <i class="fa fa-arrow-left"></i> {{ $t("back") }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6" v-loading="loading">
      <!-- Header -->
      <card class="mb-4">
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-control-label">{{ $t("modern_trade.sale_order.company") }} *</label>
              <el-select
                v-model="form.company_id"
                filterable
                :placeholder="$t('modern_trade.sale_order.company')"
                class="w-100"
                @change="onCompanyChange"
              >
                <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id"></el-option>
              </el-select>
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label class="form-control-label d-block">{{ $t("modern_trade.sale_order.sale_type") }} *</label>
              <el-select v-model="form.sale_type" class="w-100" @change="onSaleTypeChange">
                <el-option :label="$t('modern_trade.sale_order.sale_type_consignment')" :value="1"></el-option>
                <el-option :label="$t('modern_trade.sale_order.sale_type_outright')" :value="2"></el-option>
              </el-select>
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label class="form-control-label d-block">{{ $t("modern_trade.sale_order.shipping_type") }} *</label>
              <el-select v-model="form.shipping_type" class="w-100">
                <el-option :label="$t('modern_trade.sale_order.shipping_type_pack')" :value="1"></el-option>
                <el-option :label="$t('modern_trade.sale_order.shipping_type_factory')" :value="2"></el-option>
                <el-option :label="$t('modern_trade.sale_order.shipping_type_express')" :value="3"></el-option>
              </el-select>
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label class="form-control-label">{{ $t("modern_trade.sale_order.po_number") }}</label>
              <input v-model="form.po_number" class="form-control" type="text" />
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label class="form-control-label d-block">{{ $t("modern_trade.sale_order.po_date") }}</label>
              <el-date-picker
                v-model="form.po_date"
                type="date"
                value-format="yyyy-MM-dd"
                :placeholder="$t('modern_trade.sale_order.po_date')"
                class="w-100"
              ></el-date-picker>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label class="form-control-label">{{ $t("modern_trade.sale_order.po_file") }}</label>
              <input
                ref="poFileInput"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.xlsx"
                class="form-control"
                @change="onPoFileChange"
              />
              <small class="text-muted d-block">
                pdf / jpg / png / xlsx, ≤ 10MB
                <span v-if="existingPoFileName">
                  · {{ $t("modern_trade.sale_order.current_po_file") }}: <strong>{{ existingPoFileName }}</strong>
                  ({{ $t("modern_trade.sale_order.po_file_replace_hint") }})
                </span>
              </small>
            </div>
          </div>
          <div class="col-12">
            <div class="form-group mb-0">
              <label class="form-control-label">{{ $t("modern_trade.sale_order.note") }}</label>
              <input v-model="form.note" class="form-control" type="text" />
            </div>
          </div>
        </div>
      </card>

      <div class="row">
        <!-- Left: product picker -->
        <div class="col-lg-5 mb-4">
          <card class="h-100 mb-0">
            <template slot="header">
              <h3 class="mb-0">{{ $t("modern_trade.sale_order.product") }}</h3>
            </template>
            <el-input
              ref="searchInput"
              v-model="search"
              :placeholder="$t('modern_trade.sale_order.search_product')"
              :disabled="!form.company_id"
              clearable
              prefix-icon="el-icon-search"
              @input="onSearchInput"
              @keyup.enter.native="onSearchEnter"
            ></el-input>
            <small v-if="!form.company_id" class="text-warning d-block mt-2">
              <i class="fa fa-info-circle"></i> {{ $t("modern_trade.sale_order.select_company_first") }}
            </small>
            <small v-else class="text-danger d-block mt-2">
              <i class="fa fa-info-circle"></i> {{ $t("modern_trade.sale_order.no_sku_hint") }}
            </small>

            <div v-if="form.company_id" v-loading="searchLoading" class="mt-picker-list mt-3">
              <div v-if="!searchLoading && !results.length" class="text-muted text-center py-4">
                {{ $t("modern_trade.sale_order.no_product_found") }}
              </div>
              <div
                v-for="p in results"
                :key="p.id"
                class="mt-picker-row d-flex align-items-center justify-content-between"
              >
                <div class="mr-2 mt-picker-label">
                  <div>
                    {{ productLabel(p).text }}
                    <el-tag v-if="productLabel(p).noBarcode" size="mini" type="info" class="ml-1">
                      {{ $t("modern_trade.common.no_barcode") }}
                    </el-tag>
                    <span v-if="!isWmsLinked(p)" class="badge badge-danger ml-1">
                      <i class="fa fa-exclamation-triangle"></i> {{ $t("modern_trade.sale_order.no_sku_badge") }}
                    </span>
                  </div>
                  <small v-if="priceOf(p) === null" class="text-muted">…</small>
                  <small v-else-if="!priceOf(p).found" class="text-danger">
                    {{ $t("modern_trade.sale_order.no_price_badge") }}
                  </small>
                  <small v-else class="text-muted">
                    {{ $t("modern_trade.sale_order.unit_price") }}: <strong>{{ money(priceOf(p).unit_price) }}</strong>
                  </small>
                </div>
                <base-button
                  size="sm"
                  type="primary"
                  class="m-0"
                  :disabled="priceOf(p) !== null && !priceOf(p).found"
                  :title="$t('add')"
                  @click="addProduct(p)"
                >
                  <i class="fa fa-plus"></i>
                </base-button>
              </div>
            </div>
          </card>
        </div>

        <!-- Right: order lines -->
        <div class="col-lg-7 mb-4">
          <card class="h-100 mb-0">
            <template slot="header">
              <h3 class="mb-0">{{ $t("modern_trade.sale_order.items") }} ({{ lines.length }})</h3>
            </template>
            <div v-if="!lines.length" class="text-muted text-center py-4">
              {{ $t("modern_trade.sale_order.lines_empty") }}
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm align-items-center mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>{{ $t("modern_trade.sale_order.product") }}</th>
                    <th class="text-center">{{ $t("modern_trade.sale_order.qty") }}</th>
                    <th class="text-right">{{ $t("modern_trade.sale_order.unit_price") }}</th>
                    <th class="text-right">{{ $t("modern_trade.sale_order.discount") }}</th>
                    <th class="text-right">{{ $t("modern_trade.sale_order.amount") }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in lines" :key="row.product_id">
                    <td class="mt-line-label">
                      {{ productLabel(lineLabelSource(row)).text }}
                      <el-tag v-if="productLabel(lineLabelSource(row)).noBarcode" size="mini" type="info" class="ml-1">
                        {{ $t("modern_trade.common.no_barcode") }}
                      </el-tag>
                      <span v-if="row.wms_linked === false" class="badge badge-danger ml-1">
                        <i class="fa fa-exclamation-triangle"></i> {{ $t("modern_trade.sale_order.no_sku_badge") }}
                      </span>
                      <span v-if="row.no_price" class="badge badge-danger ml-1">
                        {{ $t("modern_trade.sale_order.no_price_badge") }}
                      </span>
                    </td>
                    <td class="text-center">
                      <div class="d-flex align-items-center justify-content-center mt-qty">
                        <button type="button" class="btn btn-sm btn-secondary px-2" @click="stepQty(idx, -1)">−</button>
                        <input
                          :value="row.qty"
                          type="number"
                          min="1"
                          class="form-control form-control-sm text-center mx-1"
                          @change="onQtyChange(idx, $event)"
                        />
                        <button type="button" class="btn btn-sm btn-secondary px-2" @click="stepQty(idx, 1)">+</button>
                      </div>
                    </td>
                    <td class="text-right">
                      {{ money(row.unit_price) }}
                      <small v-if="row.price_source" class="text-muted d-block">{{ sourceLabel(row.price_source) }}</small>
                    </td>
                    <td class="text-right">
                      <input
                        v-model.number="row.discount"
                        type="number"
                        min="0"
                        class="form-control form-control-sm text-right mt-discount"
                      />
                    </td>
                    <td class="text-right">{{ money(lineAmount(row)) }}</td>
                    <td class="text-center">
                      <base-button size="sm" type="danger" class="m-0" @click="removeLine(idx)">
                        <i class="fa fa-trash"></i>
                      </base-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Totals -->
            <div class="row mt-3 justify-content-end">
              <div class="col-md-7">
                <div class="d-flex justify-content-between">
                  <span class="text-muted">{{ $t("modern_trade.sale_order.sub_total") }}</span>
                  <strong>{{ money(subTotal) }}</strong>
                </div>
                <div class="d-flex justify-content-between align-items-center mt-1">
                  <span class="text-muted">{{ $t("modern_trade.sale_order.bill_discount") }}</span>
                  <input
                    v-model.number="form.bill_discount"
                    type="number"
                    min="0"
                    class="form-control form-control-sm text-right"
                    style="width:120px"
                  />
                </div>
                <div class="d-flex justify-content-between mt-1">
                  <span class="text-muted">{{ $t("modern_trade.sale_order.vat_amount") }} ({{ vatRate }}%)</span>
                  <strong>{{ money(vatAmount) }}</strong>
                </div>
                <hr class="my-2" />
                <div class="d-flex justify-content-between">
                  <span><strong>{{ $t("modern_trade.sale_order.net_amount") }}</strong></span>
                  <strong class="text-primary">{{ money(netAmount) }}</strong>
                </div>
                <small class="text-muted d-block mt-1">{{ $t("modern_trade.sale_order.estimate_note") }}</small>
              </div>
            </div>
          </card>
        </div>
      </div>

      <!-- Actions -->
      <card class="mb-4">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <small class="text-muted mr-3">{{ $t("modern_trade.sale_order.draft_hint") }}</small>
          <div v-if="isEditModernTrade">
            <base-button type="secondary" :loading="saving && !confirmingAfterSave" :disabled="saving" @click="handleSaveDraft">
              <i class="fa fa-save"></i> {{ $t("modern_trade.sale_order.save_draft") }}
            </base-button>
            <base-button type="success" :loading="saving && confirmingAfterSave" :disabled="saving" @click="handleSaveAndConfirm">
              <i class="fa fa-lock"></i> {{ $t("modern_trade.sale_order.save_and_confirm") }}
            </base-button>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { Select, Option, Input, DatePicker, Tag } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";
import { mtProductLabel } from "@/util/mt/productLabel";
import { snapshot, isDirty, addLine, setQty, saleOrderErrorKey, parseSaleOrderId } from "@/util/mt/soDraft";

const VAT_RATE = 7;
const SEARCH_DEBOUNCE_MS = 300;
// Price previews for the picker list run this many at a time (one request per
// product row; the list holds up to 50).
const PREVIEW_CONCURRENCY = 4;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

function emptyForm() {
  return {
    company_id: null,
    sale_type: 1,
    shipping_type: 1,
    po_number: "",
    po_date: "",
    note: "",
    bill_discount: 0,
  };
}

export default {
  name: "MtSaleOrderEdit",
  components: {
    RouteBreadCrumb,
    [Select.name]: Select,
    [Option.name]: Option,
    [Input.name]: Input,
    [DatePicker.name]: DatePicker,
    [Tag.name]: Tag,
  },
  data() {
    return {
      loading: false,
      saving: false,
      confirmingAfterSave: false,
      companies: [],
      soId: null,
      soNo: "",
      form: emptyForm(),
      lines: [],
      poFile: null,
      existingPoFileName: "",
      savedSnapshot: "",
      // picker
      search: "",
      results: [],
      searchLoading: false,
      searchTimer: null,
      searchSeq: 0,
      // bumps on every reprice run / company change — a stale run stops writing rows
      repriceSeq: 0,
      // false while this kept-alive instance is in the background: async work
      // that finishes then must not navigate (router.replace) the visible tab
      isActive: false,
      // `${company_id}:${product_id}` -> price-preview payload
      priceCache: {},
    };
  },
  computed: {
    ...mapGetters({
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
    isEdit() {
      return !!this.$route.params.id;
    },
    vatRate() {
      return VAT_RATE;
    },
    subTotal() {
      return this.lines.reduce((s, r) => s + this.lineAmount(r), 0);
    },
    afterDiscount() {
      return this.subTotal - (Number(this.form.bill_discount) || 0);
    },
    vatAmount() {
      return this.round2(this.afterDiscount * (VAT_RATE / 100));
    },
    netAmount() {
      return this.afterDiscount + this.vatAmount;
    },
    dirty() {
      return isDirty(this.savedSnapshot, this.currentState());
    },
  },
  created() {
    this.savedSnapshot = snapshot(this.currentState());
  },
  mounted() {
    this.isActive = true;
    window.addEventListener("beforeunload", this.onBeforeUnload);
    this.loadCompanies();
    this.init();
  },
  // The layout keeps taskbar pages alive, one instance per path (so /new,
  // /5/edit and /6/edit never share state). Leaving this page means the edits
  // were saved or the user chose to discard them (beforeRouteLeave), so a
  // cached instance starts over from the server on every return. No $route
  // watcher on purpose: an inactive cached instance would react to other
  // orders' URLs.
  activated() {
    this.isActive = true;
    if (!this.activatedOnce) {
      this.activatedOnce = true;
      return; // first activation directly follows mounted()
    }
    window.addEventListener("beforeunload", this.onBeforeUnload);
    this.init();
  },
  deactivated() {
    this.isActive = false;
    this.repriceSeq++;
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    clearTimeout(this.searchTimer);
    this.searchTimer = null;
    this.searchSeq++;
  },
  beforeDestroy() {
    this.isActive = false;
    this.repriceSeq++;
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    clearTimeout(this.searchTimer);
    this.searchSeq++;
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.dirty) return next();
    const result = await swal.fire({
      title: this.$t("modern_trade.sale_order.unsaved_title"),
      text: this.$t("modern_trade.sale_order.unsaved_text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: this.$t("modern_trade.sale_order.unsaved_leave"),
      cancelButtonText: this.$t("modern_trade.sale_order.unsaved_stay"),
    });
    next(result.value ? undefined : false);
  },
  methods: {
    // ── State ────────────────────────────────────────────────────────────────
    currentState() {
      const f = this.form;
      const file = this.poFile ? `${this.poFile.name}:${this.poFile.size}:${this.poFile.lastModified}` : "";
      return {
        company_id: f.company_id || null,
        sale_type: f.sale_type || null,
        shipping_type: f.shipping_type || null,
        po_number: (f.po_number || "").trim(),
        po_date: f.po_date || "",
        note: (f.note || "").trim(),
        bill_discount: Number(f.bill_discount) || 0,
        file,
        items: this.lines.map(l => ({
          product_id: l.product_id,
          qty: Number(l.qty) || 0,
          discount: Number(l.discount) || 0,
        })),
      };
    },
    markClean() {
      this.savedSnapshot = snapshot(this.currentState());
    },
    onBeforeUnload(e) {
      if (!this.dirty) return undefined;
      e.preventDefault();
      e.returnValue = "";
      return "";
    },
    async init() {
      this.soId = this.isEdit ? parseSaleOrderId(this.$route.params.id) : null;
      if (this.isEdit && !this.soId) {
        // /sale-orders/abc/edit — nothing to load; back to the list
        this.markClean();
        if (this.isActive) this.$router.replace({ name: "MtSaleOrders" }).catch(() => {});
        return;
      }
      this.soNo = "";
      this.form = emptyForm();
      this.lines = [];
      this.poFile = null;
      this.existingPoFileName = "";
      this.priceCache = {};
      this.search = "";
      this.results = [];
      if (this.$refs.poFileInput) this.$refs.poFileInput.value = "";
      // Clean before loading too: loadSo may redirect (not a draft) and the
      // leave guard must not see the reset as an unsaved edit.
      this.markClean();
      if (this.soId) await this.loadSo(this.soId);
      this.markClean();
    },
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ page: 1, per_page: 500, status: 1 });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.companies = d.items || [];
      } catch (err) {
        // Non-fatal — the select simply stays empty.
      }
    },
    async loadSo(id) {
      this.loading = true;
      try {
        const res = await ModernTradeService.saleOrderGet(id);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        const so = d.sale_order || d.so || null;
        // a newer init (another activation) owns the form now
        if (!so || this.soId !== id) return;
        if (so.status !== 1) {
          // Only drafts are editable (BE 409 not_draft) — send the user to the detail page,
          // but only if this tab is still the one on screen.
          if (!this.isActive) return;
          swal.fire({ title: this.$t("modern_trade.sale_order.edit_draft_only"), icon: "warning" });
          this.$router.replace({ name: "MtSaleOrderDetail", params: { id } }).catch(() => {});
          return;
        }
        this.soNo = so.so_no || "";
        this.existingPoFileName = so.po_file_name || (so.po_file_s3_key ? so.po_file_s3_key.split("/").pop() : "");
        this.form = {
          company_id: so.company_id || null,
          sale_type: so.sale_type || 1,
          shipping_type: so.shipping_type || 1,
          po_number: so.po_number || "",
          po_date: so.po_date ? String(so.po_date).slice(0, 10) : "",
          note: so.note || "",
          bill_discount: Number(so.bill_discount) || 0,
        };
        this.lines = (d.items || []).map(x => ({
          product_id: x.product_id,
          product_code: x.product_code || "",
          product_name: x.product_name || "",
          barcode: x.wms_barcode || "",
          man_pn: x.wms_man_pn || "",
          // BE fills the wms_* fields only when the product is linked to WMS.
          wms_linked: !!(x.wms_name || x.wms_man_pn || x.wms_barcode),
          qty: Number(x.qty) || 1,
          discount: Number(x.discount) || 0,
          retail_price: Number(x.retail_price) || 0,
          unit_price: Number(x.unit_price) || 0,
          price_source: "",
          no_price: false,
        }));
        // The server re-prices every line on save — show today's price now.
        await this.repriceLines();
        if (this.form.company_id) this.runSearch();
      } catch (err) {
        swal.fire({ title: this.errText(err), icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      if (this.soId) {
        this.$router.push({ name: "MtSaleOrderDetail", params: { id: this.soId } });
      } else {
        this.$router.push({ name: "MtSaleOrders" });
      }
    },

    // ── Pricing ──────────────────────────────────────────────────────────────
    async fetchPreview(productId) {
      const key = `${this.form.company_id}:${productId}`;
      if (this.priceCache[key]) return this.priceCache[key];
      const res = await ModernTradeService.pricePreview({
        company_id: this.form.company_id,
        product_id: productId,
        qty: 1,
        discount: 0,
      });
      const d = (res.data && res.data.Data && res.data.Data[0]) || { found: false };
      this.$set(this.priceCache, key, d);
      return d;
    },
    priceOf(p) {
      return this.priceCache[`${this.form.company_id}:${p.id}`] || null;
    },
    async repriceLines() {
      const seq = ++this.repriceSeq;
      const companyId = this.form.company_id;
      if (!companyId) return;
      for (const row of this.lines) {
        try {
          const d = await this.fetchPreview(row.product_id);
          // company changed / newer run started / tab left — stop writing old prices
          if (seq !== this.repriceSeq || this.form.company_id !== companyId) return;
          row.no_price = !d.found;
          if (d.found) {
            row.retail_price = Number(d.retail_price) || 0;
            row.unit_price = Number(d.unit_price) || 0;
            row.price_source = d.source || "default";
          }
        } catch (e) {
          // keep the last known price; the server prices the line on save anyway.
        }
      }
    },
    async onCompanyChange() {
      this.priceCache = {};
      await this.repriceLines();
      this.runSearch();
    },
    // Price does not depend on sale_type today, but the spec re-prices on
    // either change so the lines always match what the server will store.
    async onSaleTypeChange() {
      this.priceCache = {};
      await this.repriceLines();
      this.loadResultPrices(this.searchSeq);
    },

    // ── Picker ───────────────────────────────────────────────────────────────
    productLabel(p) {
      return mtProductLabel(p);
    },
    lineLabelSource(row) {
      return { barcode: row.barcode, man_pn: row.man_pn, code: row.product_code, name: row.product_name };
    },
    isWmsLinked(p) {
      if (!p) return true;
      if (p.wms_linked !== undefined && p.wms_linked !== null) return !!p.wms_linked;
      return !!(p.wms_product_id && Number(p.wms_product_id) > 0);
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.runSearch();
      }, SEARCH_DEBOUNCE_MS);
    },
    async runSearch() {
      if (!this.form.company_id) return;
      const seq = ++this.searchSeq;
      this.searchLoading = true;
      try {
        const params = { search: (this.search || "").trim(), status: 1, per_page: 50 };
        const res = await ModernTradeService.productsPicker(params);
        if (seq !== this.searchSeq) return;
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.results = d.items || [];
      } catch (err) {
        if (seq === this.searchSeq) this.results = [];
      } finally {
        if (seq === this.searchSeq) this.searchLoading = false;
      }
      if (seq === this.searchSeq) this.loadResultPrices(seq);
    },
    async loadResultPrices(seq) {
      const queue = this.results.filter(p => !this.priceOf(p));
      const worker = async () => {
        while (queue.length && seq === this.searchSeq) {
          const p = queue.shift();
          try {
            await this.fetchPreview(p.id);
          } catch (e) {
            // leave it unpriced; addProduct retries and reports the error.
          }
        }
      };
      const workers = [];
      for (let i = 0; i < PREVIEW_CONCURRENCY; i++) workers.push(worker());
      await Promise.all(workers);
    },
    // Enter adds the single remaining result (barcode scanners end with Enter).
    async onSearchEnter() {
      if (!this.form.company_id) return;
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
        await this.runSearch();
      }
      if (this.searchLoading || this.results.length !== 1) return;
      const added = await this.addProduct(this.results[0]);
      if (added) {
        this.search = "";
        this.runSearch();
      }
    },
    async addProduct(p) {
      if (!this.form.company_id) {
        swal.fire({ title: this.$t("modern_trade.sale_order.company_required"), icon: "warning" });
        return false;
      }
      let preview;
      try {
        preview = await this.fetchPreview(p.id);
      } catch (e) {
        swal.fire({ title: this.errText(e), icon: "error" });
        return false;
      }
      if (!preview.found) {
        swal.fire({ title: this.$t("modern_trade.sale_order.no_price"), icon: "warning" });
        return false;
      }
      const linked = this.isWmsLinked(p);
      const already = this.lines.some(l => l.product_id === p.id);
      if (!linked && !already) {
        // Warn but don't hard-block: the SKU can be linked later on the MT
        // products page before goods/delivery.
        const result = await swal.fire({
          title: this.$t("modern_trade.sale_order.no_sku_confirm_title"),
          text: this.$t("modern_trade.sale_order.no_sku_confirm_text"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
        });
        if (!result.value) return false;
      }
      this.lines = addLine(this.lines, { ...p, wms_linked: linked }, preview);
      return true;
    },

    // ── Lines ────────────────────────────────────────────────────────────────
    stepQty(idx, delta) {
      const row = this.lines[idx];
      if (!row) return;
      this.lines = setQty(this.lines, idx, (Number(row.qty) || 0) + delta);
    },
    onQtyChange(idx, e) {
      const raw = e.target.value;
      const row = this.lines[idx];
      if (raw === "" && row) {
        // An emptied box is not a request to delete — put the qty back.
        e.target.value = row.qty;
        return;
      }
      this.lines = setQty(this.lines, idx, raw);
    },
    removeLine(idx) {
      this.lines = setQty(this.lines, idx, 0);
    },
    lineAmount(row) {
      return (Number(row.unit_price) || 0) * (Number(row.qty) || 0) - (Number(row.discount) || 0);
    },
    sourceLabel(source) {
      if (source === "template") return this.$t("modern_trade.sale_order.price_source_template");
      if (source === "default") return this.$t("modern_trade.sale_order.price_source_default");
      return "";
    },

    // ── Save / confirm ───────────────────────────────────────────────────────
    onPoFileChange(e) {
      this.poFile = (e.target.files && e.target.files[0]) || null;
    },
    validateHeader() {
      if (!this.form.company_id) return this.$t("modern_trade.sale_order.company_required");
      if (!this.form.sale_type) return this.$t("modern_trade.sale_order.sale_type_required");
      if (!this.form.shipping_type) return this.$t("modern_trade.sale_order.shipping_type_required");
      if (this.poFile && this.poFile.size > MAX_FILE_BYTES) return this.$t("modern_trade.sale_order.po_file_too_large");
      return null;
    },
    validateForConfirm() {
      const err = this.validateHeader();
      if (err) return err;
      if (!(this.form.po_number || "").trim()) return this.$t("modern_trade.sale_order.po_number_required");
      if (!this.poFile && !this.existingPoFileName) return this.$t("modern_trade.sale_order.po_file_required");
      if (!this.lines.length) return this.$t("modern_trade.sale_order.items_required");
      return null;
    },
    buildFormData() {
      const fd = new FormData();
      fd.append("company_id", this.form.company_id);
      fd.append("sale_type", this.form.sale_type);
      fd.append("shipping_type", this.form.shipping_type);
      fd.append("po_number", (this.form.po_number || "").trim());
      fd.append("po_date", this.form.po_date || "");
      fd.append("note", (this.form.note || "").trim());
      fd.append("bill_discount", Number(this.form.bill_discount) || 0);
      fd.append(
        "items",
        JSON.stringify(
          this.lines.map(r => ({
            product_id: r.product_id,
            qty: Number(r.qty) || 0,
            discount: Number(r.discount) || 0,
          }))
        )
      );
      if (this.poFile) fd.append("file", this.poFile);
      return fd;
    },
    // Create (as a draft) or update the draft. Returns the sale-order id.
    async saveDraftRequest() {
      const fd = this.buildFormData();
      if (this.soId) {
        await ModernTradeService.saleOrderUpdate(this.soId, fd);
        return this.soId;
      }
      const res = await ModernTradeService.saleOrderCreate(fd, { draft: true });
      const d = (res.data && res.data.Data && res.data.Data[0]) || {};
      const so = d.sale_order || d.so || d;
      if (!so || !so.id) throw new Error("sale order id missing in response");
      this.soId = so.id;
      this.soNo = so.so_no || "";
      return so.id;
    },
    // After the first save of a new order, move to its /edit URL so the next
    // save updates it instead of creating another one.
    goToEditOf(id) {
      this.markClean();
      // saved while the tab was in the background — do not hijack the visible tab;
      // the instance (keyed by /new) restarts clean on its next activation
      if (!this.isActive) return;
      this.$router.replace({ name: "MtSaleOrderEdit", params: { id: String(id) } }).catch(() => {});
    },
    async handleSaveDraft() {
      const err = this.validateHeader();
      if (err) {
        swal.fire({ title: err, icon: "warning" });
        return;
      }
      const wasNew = !this.isEdit;
      this.saving = true;
      this.confirmingAfterSave = false;
      try {
        const id = await this.saveDraftRequest();
        swal.fire({ title: this.$t("modern_trade.sale_order.draft_saved"), icon: "success", timer: 1500, showConfirmButton: false });
        if (wasNew) {
          this.goToEditOf(id);
        } else {
          await this.init();
        }
      } catch (e) {
        swal.fire({ title: this.errText(e), icon: "error" });
      } finally {
        this.saving = false;
      }
    },
    async handleSaveAndConfirm() {
      const err = this.validateForConfirm();
      if (err) {
        swal.fire({ title: err, icon: "warning" });
        return;
      }
      const ask = await swal.fire({
        title: this.$t("modern_trade.sale_order.confirm_confirm"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm"),
        cancelButtonText: this.$t("cancel"),
      });
      if (!ask.value) return;
      const wasNew = !this.isEdit;
      this.saving = true;
      this.confirmingAfterSave = true;
      let id = null;
      try {
        id = await this.saveDraftRequest();
      } catch (e) {
        swal.fire({ title: this.errText(e), icon: "error" });
        this.saving = false;
        return;
      }
      this.markClean();
      try {
        await ModernTradeService.saleOrderConfirm(id);
        swal.fire({ title: this.$t("modern_trade.sale_order.confirmed"), icon: "success", timer: 1500, showConfirmButton: false });
        this.$router.push({ name: "MtSaleOrderDetail", params: { id } }).catch(() => {});
      } catch (e) {
        swal.fire({
          title: this.$t("modern_trade.sale_order.confirm_failed_saved"),
          text: this.errText(e),
          icon: "error",
        });
        if (wasNew) {
          this.goToEditOf(id);
        } else {
          await this.init();
        }
      } finally {
        this.saving = false;
      }
    },
    // BE puts the machine code in Msg (go-core ErrorCustom) and the text in Cause.
    errText(err) {
      const d = (err && err.response && err.response.data) || {};
      const key = saleOrderErrorKey(d.Msg);
      if (key) return this.$t(key);
      return d.Cause || d.Msg || (err && err.message) || "Error";
    },

    // ── Format ───────────────────────────────────────────────────────────────
    round2(v) {
      return Math.round((Number(v) + Number.EPSILON) * 100) / 100;
    },
    money(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.mt-picker-list {
  max-height: 60vh;
  overflow-y: auto;
  min-height: 80px;
}
.mt-picker-row {
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;
}
.mt-picker-label {
  min-width: 0;
  word-break: break-word;
}
.mt-line-label {
  white-space: normal;
  min-width: 180px;
}
.mt-qty input {
  width: 64px;
}
.mt-discount {
  width: 90px;
  display: inline-block;
}
</style>
